import { ElementRef, Injectable, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, map, takeUntil, Subject } from 'rxjs';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { GqlQueryCommandService } from './gql-query-command.service';
import { NavigationModel, navigations } from '../../root/navigations';
import { GqlQueryBuilderService } from './gql-query-builder.service';
import { IpexUserInformationService } from './ipex-user-information.service';
import { IpexUserInformation } from '../models/domain/IpexUserInformation';
import { CommonService } from './common.service';
import { SeedDataService } from '../../app-upload-cases/services/seed-data.service';
import { InsuranceCompanyModel } from '../../app-upload-cases/Models/InsuranceCompanyModel';
import { Filter } from '../models';
import { InsuranceCompanySeed } from '../models/domain/InsuranceCompanySeed';

@Injectable({
    providedIn: 'root'
})
export class SideBarNavigationService {
    private navigationList = navigations;
    private el: ElementRef;
    private renderer: Renderer2;
    private navigationSubject = new BehaviorSubject<NavigationModel[]>(navigations);
    public navigation$ = this.navigationSubject.asObservable();
    private destroy$: Subject<boolean> = new Subject<boolean>();
    constructor(
        private seedDataService: SeedDataService,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private gqlQueryCommandService: GqlQueryCommandService,
        private ipexUserInformationService: IpexUserInformationService,
        private commonService: CommonService
    ) {}

    public initSubscriptions(el: ElementRef, renderer: Renderer2) {
        this.el = el;
        this.renderer = renderer;
        this.seedDataService
            .getInsuranceCompanyData()
            .pipe(takeUntil(this.destroy$))
            .subscribe((insuranceCompanies: InsuranceCompanySeed[]) => {
                this.initSubscriptionForSideNavBadgeInformation(insuranceCompanies);
            });
        // this.initSubscriptionForUpdatingSideBarVisibility();
    }

    private getSideNavBadgeInformation(insuranceCompanies: InsuranceCompanySeed[]): Observable<any> {
        const activeCompanyIds = insuranceCompanies.filter(company => company.IsActive).map(company => company.ItemId);

        const activeInsuranceCompanyIdsFilterQuery = new Filter(`InsuranceCompanyId`, 'in', activeCompanyIds)?.value;

        const mailBoxDocProcessingComplete = `mailBoxDocProcessingComplete: ${this.gqlQueryBuilderService.buildDistinctQuery(
            SCConfig.Entity.EmailConversation,
            `{IsRead : false , DocumentProcessingStatus : { $ne: '${SCConfig.DocumentProcessingStatus.PROCESSING}' }, ${activeInsuranceCompanyIdsFilterQuery}}`,
            `{'_id': '$InsuranceCompanyId', 'Count': {'$sum': 1}}`,
            0,
            100
        )}`;
        const jarowaDocProcessingComplete = `jarowaDocProcessingComplete: ${this.gqlQueryBuilderService.buildDistinctQuery(
            SCConfig.Entity.InsuranceCase,
            `{IsFromJarowa : true , DocumentProcessingStatus : { $ne: '${SCConfig.DocumentProcessingStatus.PROCESSING}' }, IsDraft : true }`,
            `{'_id': '$CaseReferenceInfo.InsuranceComapnayId', 'Count': {'$sum': 1}}`,
            0,
            100
        )}`;
        const uploadIpexDocProcessingComplete = `uploadIpexDocProcessingComplete: ${this.gqlQueryBuilderService.buildDistinctQuery(
            SCConfig.Entity.InsuranceCase,
            `{ DocumentProcessingStatus : { $ne: '${SCConfig.DocumentProcessingStatus.PROCESSING}' }, SiteId:'78818AC2-0F17-4C4A-9825-742D8A002DB1', CaseCreationStatus:'UPCREATE', IsMarkedToDelete: false, CaseType: { $ne: 'ipex-digital' } }`,
            `{'_id': '$SiteId', 'Count': {'$sum': 1}}`,
            0,
            100
        )}`;
        return this.gqlQueryCommandService
            .gqlAggregateWatchQuery([
                mailBoxDocProcessingComplete,
                jarowaDocProcessingComplete,
                uploadIpexDocProcessingComplete
            ])
            .pipe(
                map(response => {
                    const sumCounts = (data: any[]) => (data ?? []).reduce((sum, item) => sum + (item.Count ?? 0), 0);
                    return {
                        mailBox: sumCounts(response?.data?.mailBoxDocProcessingComplete?.Data),
                        jarowa: sumCounts(response?.data?.jarowaDocProcessingComplete?.Data),
                        uploadIpex: sumCounts(response?.data?.uploadIpexDocProcessingComplete?.Data)
                    };
                })
            );
    }

    private initSubscriptionForSideNavBadgeInformation(insuranceCompanies: InsuranceCompanySeed[]) {
        this.getSideNavBadgeInformation(insuranceCompanies).subscribe(response => {
            const mailBoxFilter = this.navigationList.filter(item => item.id === 'mail-box');
            if (mailBoxFilter && mailBoxFilter.length === 1) {
                mailBoxFilter[0].badge = this.getBadgeInfo(response.mailBox);
            } else {
                console.error('Mail box navigation item not found');
            }

            const jarowaFilter = this.navigationList.filter(item => item.id === 'jarowa-integrator');
            if (jarowaFilter && jarowaFilter.length === 1) {
                jarowaFilter[0].badge = this.getBadgeInfo(response.jarowa);
            } else {
                console.error('Jarowa navigation item not found');
            }

            const filter = this.navigationList.filter(item => item.id === 'up-ipex-cases');
            if (filter && filter.length === 1) {
                filter[0].badge = this.getBadgeInfo(response.uploadIpex);
            } else {
                console.error('Upload ipex navigation item not found');
            }
            this.navigationSubject.next(this.navigationList);
            this.generateStyle();
        });
    }

    private getBadgeInfo(summation: number): any {
        return {
            title: '',
            translate: summation,
            bg: '#FFFFFF',
            fg: '#000000'
        };
    }

    private generateStyle() {
        const styleElement = this.renderer.createElement('style');
        const cssRules = `
            .nav-link-badge {
                min-width: 31px !important;
                justify-content: center;
            }
        `;
        this.renderer.setProperty(styleElement, 'innerHTML', cssRules);
        this.renderer.appendChild(this.el.nativeElement, styleElement);
    }

    private initSubscriptionForUpdatingSideBarVisibility() {
        this.ipexUserInformationService.currentUserInformation$.subscribe(userInfo => {
            this.manageQualityCheckNavBarMenu(userInfo);
        });
    }

    private manageQualityCheckNavBarMenu(userInformation: IpexUserInformation | null) {
        let navigationList = navigations;
        navigationList.forEach(nav => {
            if (nav.id === 'quality-check-list') {
                const userCanAccessQualityCheck = this.canUserAccessQualityCheck(userInformation);
                if (userCanAccessQualityCheck === true || userCanAccessQualityCheck === false) {
                    nav.isVisible = userCanAccessQualityCheck;
                    (nav as any).isShown = userCanAccessQualityCheck;
                }
            }
        });
        this.navigationSubject.next(navigationList);
    }

    public canUserAccessQualityCheck(userData: IpexUserInformation | null): boolean {
        if (userData === null) {
            return false;
        }
        if (this.commonService.getCurrentRoleUser() === SCConfig.CurrentRole.Admin) {
            return true;
        }
        if (userData.CanDoQCReview === true) {
            return true;
        }
        return false;
    }
}
